import React, { useEffect } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store";
import { routes } from "routes";

import { useResource } from "hooks/useResource";
import { resetPageScroll } from "utils/resetPageScroll";
import { clearState, setAnalysisList, setIsWelcomeModalOpen } from "store/home/homePageSlice";
import { fetchRecentAnalyses, uploadNewFile } from "store/home/thunks";
import { setKeepSearchState } from "store/search/searchbarSlice";

import { Text } from "components/Text";
import { Searchbar } from "components/_inputs/Searchbar";
import { ProfileCard } from "components/ProfileCard";
import { FileDropCard } from "components/FileDropCard";
import { RecentUpdatesTable } from "./_layouts/RecentUpdatesTable";
import { QuickAnalysis } from "components/QuickAnalysis";
import { TrainingResources } from "./_layouts/TrainingResources";
import { ExploreAnalysesCard } from "components/ExploreAnalysesCard";
import { DataSourceModal } from "common-layouts/DataSourceModal";

import { Color } from "ts/enums/color";
import { DataSourceModalType } from "ts/enums/dataSourceModalType";
import { RoleType, SocketEvent } from "@explorance/mly-types";

export const HomePage = () => {
  const { searchTerm } = useAppSelector((state) => state.search);
  const { currentUser } = useAppSelector((state) => state.auth);
  const { importModalFileId } = useAppSelector((state) => state.dataSource);
  const { socket } = useAppSelector((state) => state.wsStream);
  const state = useAppSelector((state) => state.home);

  const dispatch = useAppDispatch();

  const { getResource } = useResource();
  const history = useHistory();

  const onSearchAnalysis = () => {
    const navigationParams = {
      pathname: routes.analysisListPage,
      search: `?search=${searchTerm}`,
      state: searchTerm,
    };
    dispatch(setKeepSearchState(true));
    history.push(navigationParams);
  };

  useEffect(() => {
    dispatch(fetchRecentAnalyses());
    dispatch(setIsWelcomeModalOpen(!currentUser.skipWelcome));
    resetPageScroll();

    return () => {
      dispatch(clearState());
    };
  }, [dispatch, currentUser]);

  useEffect(() => {
    if (!socket) return;

    const handleRecentAnalysisUpdate = (recentAnalysis) => {
      if (recentAnalysis?.analysis) {
        dispatch(setAnalysisList(recentAnalysis.analysis));
      }
    };
    socket.on(SocketEvent.StreamRecentAnalysis, handleRecentAnalysisUpdate);
    socket.on(SocketEvent.ChangedAnalysis, handleRecentAnalysisUpdate);

    return () => {
      socket.off(SocketEvent.StreamRecentAnalysis, handleRecentAnalysisUpdate);
      socket.off(SocketEvent.ChangedAnalysis, handleRecentAnalysisUpdate);
    };
  }, [socket, dispatch]);

  return (
    <StyledHomePage
      className="fade-in"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <StyledLeftSection>
        <StyledHeaderSection className="fade-in-up">
          <h1>
            <Text resource="home.title" />
          </h1>
          <div>
            <Searchbar
              onSearch={onSearchAnalysis}
              placeholder={getResource("home.searchbar.placeholder")}
              customStyles={{ border: `1px solid ${Color.sky50}`, width: "auto" }}
            />
          </div>
        </StyledHeaderSection>
        <StyledCardContainer className="fade-in-up">
          <ProfileCard />
          {currentUser.roleType !== RoleType.Viewer ? (
            <StyledFileDropCardContainer>
              <FileDropCard
                onDropFile={(file) => dispatch(uploadNewFile(file)).unwrap()}
                showHelpExtension
              />
            </StyledFileDropCardContainer>
          ) : (
            <ExploreAnalysesCard />
          )}
        </StyledCardContainer>
        <TrainingResources />
        <QuickAnalysis />
      </StyledLeftSection>
      <RecentUpdatesTable
        refetchTableData={() => dispatch(fetchRecentAnalyses())}
        analysisList={state.analysisList}
      />
      <DataSourceModal
        fileUploadStatus={
          state.analysisList.find((analysis) => analysis.fileId === importModalFileId)
            ?.fileUploadStatus
        }
        importSuccessCallback={() => {
          history.push(
            routes.settingsPage(
              state.analysisList.find((analysis) => analysis.fileId === importModalFileId)
                .analysisId
            )
          );
        }}
        modalType={DataSourceModalType.ImportFile}
      />
    </StyledHomePage>
  );
};

const StyledHomePage = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: 24px;
`;

const StyledLeftSection = styled.div`
  flex-grow: 1;
  margin-right: 24px;
`;

const StyledCardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 24px;

  @media (max-width: 1365px) {
    > div {
      width: 100%;
    }
  }
`;

const StyledFileDropCardContainer = styled.div`
  margin-top: 24px;
  height: 242px;

  @media (min-width: 960px) {
    width: 430px;
  }

  @media (min-width: 1366px) {
    width: 370px;
    margin-top: 0px;
  }
`;

const StyledHeaderSection = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;

  h1 {
    font-size: 16px;
    margin-right: 24px;
    min-width: max-content;
  }
  div {
    flex-grow: 1;
  }
`;
